import { countries } from './lib/countries.js';

declare const serviceOptions: { [key: string]: string };
declare const demoOptions: { [key: string]: string };

interface Location {
	address1?: string;
	address2?: string;
	city?: string;
	province?: string;
	zip?: string;
	country?: string;
	email?: string;
	phone?: string;
	services?: string[];
	demos?: string[];
}
type StringKey =
	| 'address1'
	| 'address2'
	| 'city'
	| 'province'
	| 'zip'
	| 'country'
	| 'email'
	| 'phone';
class FFDealerLocation {
	state = {
		status: 'pending',
		step: 1,
		valid: false,
		locations: [] as Location[],
	};

	DOM: {
		form: HTMLFormElement;
		advanceButton: HTMLButtonElement;
		submitButton: HTMLButtonElement;
		spinner: HTMLDivElement;
		allInputs: NodeListOf<HTMLInputElement>;
		name: HTMLInputElement;
		url: HTMLInputElement;
		email: HTMLInputElement;
		phone: HTMLInputElement;
		section1: HTMLDivElement;
		section2: HTMLDivElement;
		section3: HTMLDivElement;
		agreementCheckbox: HTMLInputElement;
		promotionCheckbox: HTMLInputElement;
		locations: HTMLDivElement;
		addLocationButton: HTMLButtonElement;
	} = {} as any;

	constructor() {
		const form = document.querySelector('.js-serviceRequestForm');
		if (!form) return;
		this.DOM = {
			form: form as HTMLFormElement,
			section1: form.querySelector('.js-section1') as HTMLDivElement,
			section2: form.querySelector('.js-section2') as HTMLDivElement,
			section3: form.querySelector('.js-section3') as HTMLDivElement,
			advanceButton: form.querySelector('.js-advanceButton') as HTMLButtonElement,
			submitButton: form.querySelector('.js-submitButton') as HTMLButtonElement,
			spinner: form.querySelector('.js-spinner') as HTMLDivElement,
			allInputs: form.querySelectorAll('input, select, textarea'),
			name: form.querySelector('input[name=name]') as HTMLInputElement,
			url: form.querySelector('input[name=url]') as HTMLInputElement,
			email: form.querySelector('input[name=email]') as HTMLInputElement,
			phone: form.querySelector('input[name=phone]') as HTMLInputElement,
			agreementCheckbox: form.querySelector('.js-agreementCheckbox') as HTMLInputElement,
			promotionCheckbox: form.querySelector('.js-promotionCheckbox') as HTMLInputElement,
			locations: form.querySelector('.js-locations') as HTMLDivElement,
			addLocationButton: form.querySelector('.js-addLocationButton') as HTMLButtonElement,
		};
		this.events();
		this.validate();
		this.render();
	}

	events() {
		this.DOM.form.addEventListener('submit', this.formSubmit.bind(this));
		this.DOM.form.addEventListener('keyup', this.formKeyUp.bind(this));
		this.DOM.advanceButton.addEventListener('click', this.advanceClick.bind(this));
		this.DOM.addLocationButton.addEventListener('click', this.addLocation.bind(this));
		for (let i = 0; i < this.DOM.allInputs.length; i++) {
			this.DOM.allInputs[i].addEventListener('input', this.globalInput.bind(this));
		}
	}

	formSubmit(e: SubmitEvent) {
		e.preventDefault();
		if (this.state.step !== 3 || !this.validate()) return;
		if (this.state.status !== 'pending') return;
		this.state.status = 'working';
		this.render();
		const formData = new FormData();
		formData.append('action', 'dealer_location_submit');
		const data = {
			name: this.DOM.name.value,
			url: this.DOM.url.value,
			locations: this.state.locations.map((el) => ({
				...el,
				services: el.services?.join(','),
				demos: el.demos?.join(','),
			})),
		};
		formData.append('request', JSON.stringify(data));

		grecaptcha.ready(() => {
			grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit' }).then((token: string) => {
				formData.append('g-recaptcha-response', token);
				try {
					fetch(ajaxurl, {
						method: 'POST',
						body: formData,
					}).then(this.formResponse.bind(this));
				} catch (error: any) {
					console.log(error);
					this.showErrorMessage(error);
					window.scrollTo({ top: window.scrollY + 300, behavior: 'smooth' });
					this.state.status = 'error';
					this.render();
				}
			});
		});
	}

	async formResponse(res: Response) {
		const data = await res.json();
		window.scrollTo({ top: window.scrollY + 300, behavior: 'smooth' });
		// TODO: Lambda always returns 200
		if (res.status === 200) {
			this.showSuccessMessage(data.message);
			this.state.status = 'success';
		} else {
			this.showErrorMessage(data.message);
			this.state.status = 'error';
		}
		this.render();
	}

	formKeyUp() {
		this.validate();
		this.render();
	}

	globalInput() {
		this.validate();
		this.render();
	}

	advanceClick() {
		this.validate();
		if (!this.state.valid) return;
		this.state.step = this.state.step + 1;
		if (this.state.step === 3) {
			this.addLocation();
		}
		this.validate();
		this.render();
		this.scrollToStep();
	}

	render() {
		this.renderSteps();
		this.renderFooter();
	}

	renderSteps() {
		if (this.state.step > 1) {
			this.DOM.section2.classList.remove('js-hide');
		} else {
			this.DOM.section2.classList.add('js-hide');
		}
		if (this.state.step > 2) {
			this.DOM.section3.classList.remove('js-hide');
		} else {
			this.DOM.section3.classList.add('js-hide');
		}
	}

	renderFooter() {
		if (this.state.valid) {
			this.DOM.advanceButton.disabled = false;
		} else {
			this.DOM.advanceButton.disabled = true;
		}
		if (this.state.valid && this.state.status === 'pending') {
			this.DOM.submitButton.disabled = false;
		} else {
			this.DOM.submitButton.disabled = true;
		}
		if (this.state.step === 3) {
			this.DOM.advanceButton.classList.add('js-hide');
			this.DOM.submitButton.classList.remove('js-hide');
		} else {
			this.DOM.advanceButton.classList.remove('js-hide');
			this.DOM.submitButton.classList.add('js-hide');
		}
		if (this.state.status === 'working') {
			this.DOM.spinner.classList.remove('js-hide');
		} else {
			this.DOM.spinner.classList.add('js-hide');
		}
	}

	validate() {
		let valid = true;
		if (this.state.step >= 1) {
			const step1Valid = this.validateStep1();
			if (!step1Valid) valid = false;
		}
		if (this.state.step >= 2) {
			const step2Valid = this.validateStep2();
			if (!step2Valid) valid = false;
		}
		if (this.state.step >= 3) {
			const step3Valid = this.validateStep3();
			if (!step3Valid) valid = false;
		}

		this.state.valid = valid;
		return valid;
	}

	validateStep1() {
		if (!this.DOM.agreementCheckbox.checked) return false;
		if (!this.DOM.promotionCheckbox.checked) return false;
		return true;
	}

	validateStep2() {
		if (!this.DOM.name.value) return false;
		return true;
	}

	validateStep3() {
		for (let i = 0; i < this.state.locations.length; i++) {
			const location = this.state.locations[i];
			if (!location.city) return false;
			if (!location.zip) return false;
			if (!location.country) return false;
		}
		return true;
	}

	addLocation() {
		const locationId = this.state.locations.length;
		this.state.locations.push({
			email: this.DOM.email.value,
			phone: this.DOM.phone.value,
		});
		const location = document.createElement('div');
		location.classList.add('location', 'border', 'border-gray-300', 'rounded-md', '[&_+_&]:mt-4');
		location.innerHTML = `
<div class="px-4 pt-4 pb-2">
  <h3 class="font-bold">${locationId === 0 ? 'Primary Location' : `Location ${locationId + 1}`}</h3>
</div>
<div class="formHr"></div>
<div class="formRow">
  <div class="formCol">
    <label class="inputLabel">Address</label>
    <input class="textInput" type="text" name="location${locationId}_address1"/>
    <input class="textInput mt-2" type="text" name="location${locationId}_address2"/>
  </div>
</div>
<div class="formRow">
  <div class="formCol">
    <label class="inputLabel">City*</label>
    <input class="textInput" type="text" name="location${locationId}_city" required />
  </div>
  <div class="formCol">
    <label class="inputLabel">Province</label>
    <input class="textInput" type="text" name="location${locationId}_province" />
  </div>
  <div class="formCol">
    <label class="inputLabel">Zip*</label>
    <input class="textInput" type="text" name="location${locationId}_zip" required/>
  </div>
  <div class="formCol">
    <label class="inputLabel">Country*</label>
    <select class="textInput" name="location${locationId}_country" required>
      <option value="">Select</option>
      ${Object.entries(countries)
				.map(([code, name]) => `<option value="${code}">${name}</option>`)
				.join('')}
    </select>
  </div>
</div>
<div class="formRow">
  <div class="formCol">
    <label class="inputLabel">Email</label>
    <input class="textInput" type="email" name="location${locationId}_email" value="${this.DOM.email.value}"/>
  </div>
  <div class="formCol">
    <label class="inputLabel">Phone Number</label>
    <input class="textInput" type="tel" name="location${locationId}_phone" value="${this.DOM.phone.value}"/>
  </div>
</div>
<div class="formRow">
  <div class="formCol">
    <label class="inputLabel">Services</label>
    <div class="flex flex-col gap-2">
    ${Object.entries(serviceOptions)
			.map(
				([code, name]) => `
      <label class="radioWrapper" for="location${locationId}_service_${code}">
        <input class="hiddenRadio offscreen" type="checkbox" id="location${locationId}_service_${code}" name="location${locationId}_services" value="${code}"/>
        <div class="fauxRadioBox">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
        </div>
        <span class="radioLabel">${name}</span>
      </label>
      `
			)
			.join('')}
    </div>
  </div>
  <div class="formCol">
    <label class="inputLabel">Demos</label>
    <div class="flex flex-col gap-2">
    ${Object.entries(demoOptions)
			.map(
				([code, name]) => `
      <label class="radioWrapper" for="location${locationId}_demo_${code}">
        <input class="hiddenRadio offscreen" type="checkbox" id="location${locationId}_demo_${code}" name="location${locationId}_demos" value="${code}"/>
        <div class="fauxRadioBox">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
        </div>
        <span class="radioLabel">${name}</span>
      </label>
      `
			)
			.join('')}
    </div>
  </div>
</div>
    `;
		const allLocationInputs = location.querySelectorAll(
			'input[type=text], input[type=email], input[type=tel], select'
		);
		for (let i = 0; i < allLocationInputs.length; i++) {
			allLocationInputs[i].addEventListener('input', this.locationInput.bind(this));
			allLocationInputs[i].addEventListener('input', this.globalInput.bind(this));
		}
		const allLocationCheckboxes = location.querySelectorAll('input[type=checkbox]');
		for (let i = 0; i < allLocationCheckboxes.length; i++) {
			allLocationCheckboxes[i].addEventListener('change', this.locationCheckbox.bind(this));
			allLocationCheckboxes[i].addEventListener('change', this.globalInput.bind(this));
		}
		this.DOM.locations.appendChild(location);
	}

	locationInput(e: Event) {
		const input = e.target as HTMLInputElement;
		const locationIdString = input.name.split('_')[0];
		const locationId = Number(locationIdString.split('location')[1]);
		const name = input.name.split('_')[1];
		if (isNaN(locationId)) return;
		if (locationId >= this.state.locations.length) return;
		this.state.locations[locationId][name as StringKey] = input.value;
	}

	locationCheckbox(e: Event) {
		const checkbox = e.target as HTMLInputElement;
		const locationIdString = checkbox.name.split('_')[0];
		const locationId = Number(locationIdString.split('location')[1]);
		const field = checkbox.name.split('_')[1];
		if (isNaN(locationId)) return;
		if (locationId >= this.state.locations.length) return;
		const existing = this.state.locations[locationId][field as 'services' | 'demos'] || [];
		this.state.locations[locationId][field as 'services' | 'demos'] = checkbox.checked
			? [...existing, checkbox.value]
			: existing.filter((value: string) => value !== checkbox.value);
	}

	showSuccessMessage(message: string) {
		const section = document.createElement('div');
		section.classList.add('section', 'messageSection', 'success');
		const html = `
<div class="sectionHeader">
  <h2 class="sectionHl">Success!</p>
</div>
<div class="sectionBody">
  <div class="formRow">
    <div class="formCol">
      ${message}
    </div>
  </div>
</div>
`;
		section.innerHTML = html;
		this.DOM.form.appendChild(section);
	}

	showErrorMessage(message: string) {
		const section = document.createElement('div');
		section.classList.add('section', 'messageSection', 'error');
		const html = `
<div class="sectionHeader">
  <h2 class="sectionHl">Error</p>
</div>
<div class="sectionBody">
  <div class="formRow">
    <div class="formCol">
      <p>Uh oh. There was an issue with the form submission:</p>
      ${message ? `<pre>${message}</pre>` : ''}
      <p><a href="mailto:support@freeflysystems.com">Email support</a> for help with your service request.</p>
    </div>
  </div>
</div>
`;
		section.innerHTML = html;
		this.DOM.form.appendChild(section);
	}

	scrollToStep() {
		const stepEl = this.DOM[
			`section${this.state.step - 1}` as keyof typeof this.DOM
		] as HTMLDivElement;
		if (!stepEl) return;
		const height = Math.min(300, stepEl.getBoundingClientRect().height);
		window.scrollTo({ top: window.scrollY + height, behavior: 'smooth' });
	}
}

const _ffDealerLocation = new FFDealerLocation();
